/**
 * FoundationSlaBadge — the "N approvals past SLA" pointer.
 *
 * iter295: moved off the Settings gear. A dot on the gear told you
 * something was wrong but not where, so the count now lights up the
 * actual path — Settings → Integrations → Foundation Approval Queue —
 * and the queue panel itself carries the detail (ages, per-row markers).
 *
 * `useFoundationSla` is polled once, in Settings, and the count is passed
 * down; two components rendering the same number must not mean two
 * pollers hitting the endpoint.
 */
import { useEffect, useState } from "react";
import { getFoundationSlaAlerts } from "@/lib/api";

// 60 s — the SLA threshold is in hours and the backend already checks
// hourly; this poll only surfaces the resulting state.
const POLL_INTERVAL_MS = 60_000;

export function useFoundationSla(isVisible = false) {
  const [state, setState] = useState({ staleCount: 0, oldestAgeHours: null });

  useEffect(() => {
    if (!isVisible) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await getFoundationSlaAlerts();
        if (!cancelled) {
          setState({ staleCount: data?.stale_count || 0, oldestAgeHours: data?.oldest_age_hours ?? null });
        }
      } catch {
        // A broken badge must never break the nav.
        if (!cancelled) setState({ staleCount: 0, oldestAgeHours: null });
      }
    };
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [isVisible]);

  const { staleCount, oldestAgeHours } = state;
  const ageLabel = oldestAgeHours == null
    ? ""
    : oldestAgeHours < 24 ? `${oldestAgeHours.toFixed(1)} h` : `${(oldestAgeHours / 24).toFixed(1)} d`;
  const title = staleCount > 0
    ? `${staleCount} Foundation approval${staleCount === 1 ? "" : "s"} past SLA${ageLabel ? ` (oldest ${ageLabel})` : ""} — open the Foundation Approval Queue to review.`
    : "";
  return { staleCount, oldestAgeHours, ageLabel, title };
}

export default function FoundationSlaBadge({ count = 0, title = "", testId = "foundation-sla-badge" }) {
  if (count <= 0) return null;
  return (
    <span
      title={title}
      aria-label={title}
      data-testid={testId}
      className="inline-flex items-center gap-1 rounded-full bg-red-600 text-white text-[10px] font-bold leading-none px-1.5 py-0.5 tabular-nums animate-pulse"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-white/90" aria-hidden="true" />
      {count > 99 ? "99+" : count}
    </span>
  );
}
